import { useState, useEffect, useContext } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NotificationsIcon from "@mui/icons-material/Notifications";
import GroupIcon from "@mui/icons-material/Group";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

// My Files
import firebase from "../../../firebase";
import { DatabaseContext, UserContext } from "../../../context";
import UnsavedChangesNotice from "../../UnsavedChangesNotice";
import SuccessDialog from "../../SuccessDialog";
import FilePermissionsDialog from "../uploading/FilePermissionsDialog";
import EditFileNotificationsDialog from "./EditFileNotificationsDialog";
import LinkFileToEventDialog from "../uploading/LinkFileToEventDialog";
import {
    shouldFileBeVisibleToUser,
    getAncestorHubs,
    canManageFile,
} from "../../../utility";

const relatedSettingButtonSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    minWidth: 0,
    p: 1.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
    textAlign: "left",
    justifyContent: "flex-start",
    "&:hover": {
        borderColor: "rgba(0, 149, 61, 0.35)",
        boxShadow: "0 3px 12px rgba(20, 35, 25, 0.07)",
    },
};

const toggleLabel = (isOn) => (isOn ? "turned on" : "turned off");

// Describes what a save actually changed, for the confirmation dialog
function buildSettingsDetails(previous, next) {
    const details = [];

    if (next.displayName !== previous.displayName) {
        details.push(`Display name changed to "${next.displayName}"`);
    }
    if (next.allowDownloads !== previous.allowDownloads) {
        details.push(`Downloads ${toggleLabel(next.allowDownloads)}`);
    }
    if (next.allowComments !== previous.allowComments) {
        details.push(`Comments ${toggleLabel(next.allowComments)}`);
    }
    if (next.allowSharing !== previous.allowSharing) {
        details.push(`Sharing ${toggleLabel(next.allowSharing)}`);
    }

    return details;
}

function AvailabilityRow({
    icon,
    title,
    description,
    checked,
    onChange,
    name,
    divided = false,
    disabled = false,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    placeItems: "center",
                    color: "text.secondary",
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                name={name}
                color="primary"
                disabled={disabled}
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

export default function EditFileDialog(props) {
    const { users, calendars, notifications, groups, folders } =
        useContext(DatabaseContext);

    const { currentUserData } = useContext(UserContext);
    const isExternalLink = props.file?.type === "link";

    const [linkedEvents, setLinkedEvents] = useState([]);
    const [displayName, setDisplayName] = useState("");
    const [allowComments, setAllowComments] = useState(true);
    const [allowSharing, setAllowSharing] = useState(false);
    const [allowDownloads, setAllowDownloads] = useState(true);
    // Snapshot of the above as last written, used to detect unsaved edits
    const [savedSettings, setSavedSettings] = useState(null);
    const [confirmation, setConfirmation] = useState(null);

    const [selectedUsers, setSelectedUsers] = useState([]);
    // const [defaultUsers, setDefaultUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);

    const [managers, setManagers] = useState([]);
    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [notificationsDialogOpen, setNotificationsDialogOpen] =
        useState(false);

    const [ancestorHubs, setAncestorHubs] = useState([]);

    const canManage = canManageFile(props.file, currentUserData);

    // The name and the three toggles are held back until Save, so they are seeded
    // once when the dialog opens. Everything in the second effect is derived from
    // the live database and re-runs freely; keeping the two apart stops an
    // incoming update from wiping out edits the user hasn't saved yet.
    useEffect(() => {
        if (props.open && props.file) {
            // Save writes all four fields, and Firebase rejects undefined, so
            // coerce here. allowSharing in particular is absent on older records.
            const initial = {
                displayName: props.file.displayName || "",
                allowComments: props.file.allowComments !== false,
                allowDownloads: props.file.allowDownloads !== false,
                allowSharing: props.file.allowSharing === true,
            };

            setDisplayName(initial.displayName);
            setAllowComments(initial.allowComments);
            setAllowDownloads(initial.allowDownloads);
            setAllowSharing(initial.allowSharing);
            setSavedSettings(initial);
        }
        // eslint-disable-next-line
    }, [props.open, props.file?.key]);

    const hasUnsavedChanges =
        savedSettings !== null &&
        (displayName !== savedSettings.displayName ||
            allowComments !== savedSettings.allowComments ||
            allowDownloads !== savedSettings.allowDownloads ||
            allowSharing !== savedSettings.allowSharing);

    useEffect(() => {
        if (props.file && props.folder) {
            setLinkedEvents(props.file.events);

            let usersArray = [];
            let managersArray = [];

            // Get user objects from database
            users.forEach((user) => {
                // Reset this value in case previously set
                if (props.file.managers.includes(user.key)) {
                    managersArray.push(user);
                } else if (
                    shouldFileBeVisibleToUser(props.file, groups, user)
                ) {
                    usersArray.push(user);
                }
            });

            setManagers(managersArray);

            // Get ancestor hubs, these are used for the groups
            let ancestors = getAncestorHubs(
                props.file,
                props.folder,
                folders,
                currentUserData,
            );

            let selectedGroupsArray = [];

            // Get the groups for these ancestors
            ancestors.forEach((ancestorHub) => {
                let matchingGroups = groups.filter(
                    (g) => g.hub === ancestorHub.key,
                );

                // Set the selected groups
                matchingGroups.forEach((group) => {
                    if (
                        props.file.groups.filter((g) => g === group.key)
                            .length > 0
                    ) {
                        group.didSelect = true;
                        selectedGroupsArray.push(group);
                    } else {
                        group.didSelect = false;
                    }
                });

                ancestorHub.groups = matchingGroups;
            });

            setSelectedGroups(selectedGroupsArray);
            setAncestorHubs(ancestors);
            setSelectedUsers(usersArray);

            // Find calendars that user can see
            let calendarsArray = [];

            calendars.forEach((calendar) => {
                let shouldAdd = false;

                // User should be able to see calendar if it's their personal one, or if they are uploading a file
                // to the hub for that calendar
                if (calendar.user === currentUserData.key) {
                    shouldAdd = true;
                } else if (calendar.hub === props.folder.key) {
                    shouldAdd = true;
                } else if (props.folder.ancestors.includes(calendar.hub)) {
                    shouldAdd = true;
                }

                if (shouldAdd) {
                    calendarsArray.push(calendar);
                }
            });

            setAvailableCalendars(calendarsArray);
        }
        // eslint-disable-next-line
    }, [currentUserData, calendars, props.file]);

    const handleClose = () => {
        props.onClose();
    };

    // Clicking the backdrop is easy to do by accident, so it only closes the
    // dialog when there is nothing waiting to be saved
    const handleDialogClose = (event, reason) => {
        if (reason === "backdropClick" && hasUnsavedChanges) {
            return;
        }

        handleClose();
    };

    function handleSaveClick() {
        // Nothing to write, but still close rather than leaving the button dead
        if (!canManage || !hasUnsavedChanges) {
            props.onClose();
            return;
        }

        const nextSettings = {
            displayName: displayName,
            allowComments: allowComments,
            allowDownloads: allowDownloads,
            allowSharing: allowSharing,
        };

        const details = buildSettingsDetails(savedSettings, nextSettings);

        let ref = firebase.database().ref("files").child(props.file.key);

        // Update the file record in firebase
        ref.update(nextSettings);

        setSavedSettings(nextSettings);

        // Change the file name in existing notifications

        if (displayName !== savedSettings.displayName) {
            notifications.forEach((notification) => {
                if (notification.file === props.file.key) {
                    var notificationRef = firebase
                        .database()
                        .ref("notifications")
                        .child(notification.key);

                    notificationRef.update({ title: displayName });
                }
            });
        }

        // Stay open behind the confirmation, which dismisses the dialog
        setConfirmation(details);
    }

    const handleConfirmationClose = () => {
        setConfirmation(null);
        props.onClose();
    };

    function handleChangeDisplayName(event) {
        setDisplayName(event.target.value);
    }

    function handleChangeSwitch(event) {
        // Held in local state until Save, alongside the display name
        if (event.target.name === "downloads") {
            setAllowDownloads(event.target.checked);
        } else if (event.target.name === "comments") {
            setAllowComments(event.target.checked);
        } else if (event.target.name === "sharing") {
            setAllowSharing(event.target.checked);
        }
    }

    function handleEventsClick() {
        setEventDialogOpen(true);
    }

    function handlePermissionsClick() {
        setPermissionsDialogOpen(true);
    }

    function handleClosePermissionsDialog(users, groups) {
        setSelectedGroups(groups);
        setSelectedUsers(users);
        setPermissionsDialogOpen(false);
    }

    function handleNotificationsClick() {
        setNotificationsDialogOpen(true);
    }

    function handleEventSelectionChange(selectedEvents) {
        setLinkedEvents(selectedEvents);
    }

    function handleCloseEventDialog(selectedEvents) {
        setLinkedEvents(selectedEvents);
        setEventDialogOpen(false);
    }

    return (
        <>
            <Dialog
                onClose={handleDialogClose}
                aria-labelledby="edit-file-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "rgba(0, 149, 61, 0.09)",
                        }}
                    >
                        <EditOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="edit-file-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Edit file
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {canManage
                                ? "Update the file details and availability options."
                                : "You do not have permission to change this file."}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close edit file"
                        onClick={handleClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1.5, fontWeight: 800 }}
                        >
                            File details
                        </Typography>
                        <TextField
                            id="edit-file-display-name"
                            value={displayName}
                            onChange={handleChangeDisplayName}
                            label="Display name"
                            fullWidth
                            multiline
                            maxRows={3}
                            variant="outlined"
                            disabled={!canManage}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                        >
                            File settings
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1,
                            }}
                        >
                            <ButtonBase
                                onClick={handleEventsClick}
                                sx={relatedSettingButtonSx}
                            >
                                <EventNoteIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Events
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Linked events
                                    </Typography>
                                </Box>
                                <Chip
                                    label={linkedEvents.length}
                                    size="small"
                                    sx={{ height: 24 }}
                                />
                            </ButtonBase>

                            <ButtonBase
                                onClick={handleNotificationsClick}
                                sx={relatedSettingButtonSx}
                            >
                                <NotificationsIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Notifications
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Send Notifications
                                    </Typography>
                                </Box>
                                <SendOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: "text.disabled" }}
                                />
                            </ButtonBase>

                            <ButtonBase
                                onClick={handlePermissionsClick}
                                sx={relatedSettingButtonSx}
                            >
                                <GroupIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Access
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Users and groups
                                    </Typography>
                                </Box>
                                <Chip
                                    label={
                                        selectedUsers.length + managers.length
                                    }
                                    size="small"
                                    sx={{ height: 24 }}
                                />
                            </ButtonBase>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                        >
                            Permissions
                        </Typography>
                        <Box
                            sx={{
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                                overflow: "hidden",
                                backgroundColor: "background.paper",
                            }}
                        >
                            {!isExternalLink && (
                                <AvailabilityRow
                                    icon={<DownloadOutlinedIcon />}
                                    title="Downloads"
                                    description="Allow users to download this file."
                                    checked={allowDownloads}
                                    onChange={handleChangeSwitch}
                                    name="downloads"
                                    disabled={!canManage}
                                />
                            )}
                            <AvailabilityRow
                                icon={<ChatBubbleOutlineIcon />}
                                title="Comments"
                                description="Allow users to discuss this file."
                                checked={allowComments}
                                onChange={handleChangeSwitch}
                                name="comments"
                                divided={!isExternalLink}
                                disabled={!canManage}
                            />
                            {!isExternalLink && (
                                <AvailabilityRow
                                    icon={<ShareOutlinedIcon />}
                                    title="Sharing"
                                    description="Allow users to share a public download link to this file."
                                    checked={allowSharing}
                                    onChange={handleChangeSwitch}
                                    name="sharing"
                                    divided
                                    disabled={!canManage}
                                />
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                    {hasUnsavedChanges && <UnsavedChangesNotice />}
                    <Button color="inherit" onClick={handleClose}>
                        {hasUnsavedChanges ? "Discard changes" : "Close"}
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSaveClick}
                        disabled={
                            !canManage ||
                            !hasUnsavedChanges ||
                            displayName.trim() === ""
                        }
                    >
                        Save changes
                    </Button>
                </DialogActions>
            </Dialog>
            <LinkFileToEventDialog
                open={eventDialogOpen}
                onClose={handleCloseEventDialog}
                selectionChanged={handleEventSelectionChange}
                selectedEvents={linkedEvents}
                file={props.file}
                calendars={availableCalendars}
            />
            <FilePermissionsDialog
                open={permissionsDialogOpen}
                onClose={handleClosePermissionsDialog}
                // defaultUsers={defaultUsers}
                selectedUsers={selectedUsers}
                managers={managers}
                ancestorHubs={ancestorHubs}
                selectedGroups={selectedGroups}
                file={props.file}
            />
            <EditFileNotificationsDialog
                open={notificationsDialogOpen}
                onClose={() => setNotificationsDialogOpen(false)}
                file={props.file}
                selectedUsers={selectedUsers}
                managers={managers}
                ancestorHubs={ancestorHubs}
                currentUserData={currentUserData}
            />
            <SuccessDialog
                open={confirmation !== null}
                onClose={handleConfirmationClose}
                title="Changes saved"
                details={confirmation || []}
            />
        </>
    );
}
